/**
 * Client-side twin of the consumer's `humanize.ts:formatFieldValue` (main-consumer)
 * and the backend's `FieldValueFormatter.format` (main-backend) — used to render
 * an additional (custom) field value in the email / document builder PREVIEW so
 * the admin sees what the recipient will actually get, instead of a raw ISO
 * date / `true` / bare number.
 *
 * Unlike the send path (which infers the type from the runtime BSON value), the
 * FE receives values as JSON (dates arrive as ISO strings, booleans as bool),
 * so we drive formatting off the field DEFINITION's `dataType` — which is
 * exactly what makes DATE vs DATE_TIME unambiguous here, matching the send's
 * `__dateTimeFields` / `forceDateTime` fix.
 *
 * KEEP IN SYNC with main-consumer/src/lib/humanize.ts (formatFieldValue,
 * formatCurrency, formatNumber, localizedBoolean) and main-backend
 * FieldValueFormatter.
 */
import type { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { formatCurrencyValue, formatGroupedDecimal } from "./currency";
import { parseToUTCDate } from "./parser";
import { parseUrlField, isSafeHttpUrl } from "./urlField";
import { escapeHtml } from "./html-paste-sanitizer";
import { formatApptDate, formatApptTime } from "@/components/A4Builder/appointment-record";

/**
 * Localized Yes/No labels for BOOLEAN values, keyed by primary language subtag.
 * English is the fallback. KEEP IN SYNC with main-consumer humanize.BOOLEAN_LABELS
 * / main-backend FieldValueFormatter.BOOLEAN_LABELS / companion-record.ts.
 */
const BOOLEAN_LABELS: Record<string, readonly [string, string]> = {
  bg: ["Да", "Не"], ca: ["Sí", "No"], cs: ["Ano", "Ne"], cy: ["Ie", "Na"],
  da: ["Ja", "Nej"], de: ["Ja", "Nein"], el: ["Ναι", "Όχι"], en: ["Yes", "No"],
  es: ["Sí", "No"], et: ["Jah", "Ei"], eu: ["Bai", "Ez"], fi: ["Kyllä", "Ei"],
  fr: ["Oui", "Non"], ga: ["Sea", "Ní hea"], gd: ["Tha", "Chan eil"],
  gl: ["Si", "Non"], hr: ["Da", "Ne"], hu: ["Igen", "Nem"], is: ["Já", "Nei"],
  it: ["Sì", "No"], lb: ["Jo", "Nee"], lt: ["Taip", "Ne"], lv: ["Jā", "Nē"],
  mt: ["Iva", "Le"], nb: ["Ja", "Nei"], nl: ["Ja", "Nee"], pl: ["Tak", "Nie"],
  pt: ["Sim", "Não"], rm: ["Gea", "Na"], ro: ["Da", "Nu"], sk: ["Áno", "Nie"],
  sl: ["Da", "Ne"], sq: ["Po", "Jo"], sr: ["Da", "Ne"], sv: ["Ja", "Nej"],
};

function primaryLang(lang: string | null | undefined): string {
  return typeof lang === "string" && lang.length > 0
    ? lang.split("-")[0]!.toLowerCase()
    : "en";
}

/** Localized Yes/No in `lang` (de → "Ja"/"Nein"). */
export function localizedBoolean(
  value: boolean,
  lang: string | null | undefined,
): string {
  const pair = BOOLEAN_LABELS[primaryLang(lang)] ?? BOOLEAN_LABELS["en"]!;
  return value ? pair[0] : pair[1];
}

/**
 * Build the preview formatting context (field-name → definition map + event
 * timezone) passed to the variable replacer so `{{additionalFields.x}}` renders
 * by declared type. Structurally matches `PreviewFormatContext` in
 * variable-replacer.ts (kept import-free to avoid a module cycle).
 */
export function buildPreviewFormatContext(
  fields: readonly Field[],
  timeZone: string | null | undefined,
  /** Resolved render language (effLang) for typed-value formatting; omit to fall
   *  back to the guest's own language inside the replacer. */
  lang?: string | null,
): { fieldsByName: Map<string, Field>; timeZone: string | null; lang: string | null } {
  const fieldsByName = new Map<string, Field>();
  for (const f of fields) fieldsByName.set(f.name, f);
  return { fieldsByName, timeZone: timeZone ?? null, lang: lang ?? null };
}

/** True when the instant sits exactly on UTC midnight (a stored DATE value). */
function isUtcMidnight(d: Date): boolean {
  return (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

/**
 * Format an additional-field value for preview by its declared `dataType`,
 * mirroring the real send:
 *   BOOLEAN            → localized Yes/No
 *   DATE              → long-form date rendered in UTC (calendar day never shifts)
 *   DATE_TIME         → long date + short time in the event tz (time kept even at
 *                       00:00 — the field is DECLARED DATE_TIME)
 *   CURRENCY          → localized money via the field's ISO 4217 code
 *   DOUBLE            → locale-grouped decimal (no symbol)
 *   everything else   → String(value) (STRING/EMAIL/URL/SELECT/LONG/MULTI_SELECT)
 * Null/undefined/empty → ''.
 *
 * `field` may be undefined (unknown token) → falls back to String(value), so an
 * additional field with no matching definition never throws.
 */
export function formatAdditionalFieldValue(
  value: unknown,
  field: Field | undefined,
  lang: string | null | undefined,
  timeZone: string | null | undefined,
  /** HTML context → a URL field renders as an <a> anchor (matching the sent
   *  HTML body); plain-text / canvas contexts get "label (url)". */
  asHtml = false,
): string {
  if (value === null || value === undefined || value === "") return "";
  const locale = lang && lang.length > 0 ? lang : "en";
  const dt = field?.dataType;

  // IMAGE fields hold a backend-minted {key, contentType, size} object — a
  // text rendering makes no sense (the image block / af-image URL resolver is
  // the display path). Early return before any per-type formatting.
  if (dt === DataType.IMAGE) return "";

  // B2 object guard: any non-Date, non-Array object (e.g. an IMAGE value on a
  // field whose definition is missing, or future structured values) must never
  // fall through to String(value) → "[object Object]" in a rendered body.
  if (
    typeof value === "object" &&
    !(value instanceof Date) &&
    !Array.isArray(value)
  ) {
    return "";
  }

  // URL field stored as `[label](url)` or a bare URL → anchor in HTML, else
  // "label (url)". Mirrors the consumer renderUrlFieldHtml / urlFieldToPlainText.
  if (dt === DataType.URL && typeof value === "string") {
    const { url, label } = parseUrlField(value);
    if (url && isSafeHttpUrl(url)) {
      const text = label || url;
      return asHtml
        ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`
        : label
          ? `${label} (${url})`
          : url;
    }
    return String(value);
  }

  if (typeof value === "boolean" || dt === DataType.BOOLEAN) {
    const b =
      typeof value === "boolean" ? value : String(value).toLowerCase() === "true";
    return localizedBoolean(b, lang);
  }

  if (dt === DataType.DATE || dt === DataType.DATE_TIME) {
    // parseToUTCDate strips the `…Z[UTC]` IANA suffix the backend appends, which
    // raw `new Date` rejects. Non-date payloads fall back to String(value).
    const d = parseToUTCDate(String(value));
    if (d) {
      const iso = d.toISOString();
      const forceDateTime = dt === DataType.DATE_TIME;
      const dateOnly = !forceDateTime && isUtcMidnight(d);
      if (dateOnly) return formatApptDate(iso, lang, "UTC");
      const datePart = formatApptDate(iso, lang, timeZone);
      const timePart = formatApptTime(iso, lang, timeZone);
      return datePart && timePart ? `${datePart} ${timePart}` : datePart || timePart;
    }
  }

  if (dt === DataType.CURRENCY && typeof value === "number") {
    return formatCurrencyValue(value, field?.currencyCode, locale);
  }
  if (dt === DataType.DOUBLE && typeof value === "number") {
    return formatGroupedDecimal(value, locale);
  }

  return String(value);
}
